// File: app/src/components/session/CopySessionMenu.js
//
// The single place the two clipboard exports are named and offered (#43).
// Khronos can put two very different things on the clipboard and the old UI
// exposed them under the same word "Copy" in different corners of the app:
//
//   Summary — readable recap (times, actuals, effort, notes) for sharing.
//   Plan    — YAML the app can load back in via Load Session / Import.
//
// Every copy affordance renders this menu so the wording, ordering and icons
// are identical wherever a session can be copied from.
//
// Publishing a link to a session is NOT here, by design — see
// components/share/ShareSessionButton. Both of these copy text to your own
// clipboard; a link is published for someone else, and burying it behind the
// word "Copy" meant nobody found it.

import React, { useState } from "react";
import { Copy, Check, FileText, FileCode } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import {
  copySummaryToClipboard,
  copySessionToClipboard,
} from "../../utils/sessionShare";
export const COPY_SUMMARY_LABEL = "Copy summary";
export const COPY_PLAN_LABEL = "Copy session plan";

/**
 * The two copy actions as bare menu items, for a caller that already has a
 * DropdownMenu open (the history row's overflow menu).
 *
 * Split out rather than duplicated so this file stays what its header claims:
 * the single place the two exports are named, worded and ordered. A nested
 * DropdownMenu inside a DropdownMenuItem does not behave, so composition has
 * to happen at the item level.
 */
export const CopySessionItems = ({
  plan,
  notes,
  rating,
  durationMinutes,
  onCopied,
}) => {
  if (!plan) return null;
  const done = (ok) => onCopied?.(ok);
  return (
    <>
      <DropdownMenuItem
        onSelect={async () =>
          done(
            await copySummaryToClipboard(plan, {
              notes,
              rating,
              durationMinutes,
            }),
          )
        }
      >
        <FileText className="h-3.5 w-3.5 mr-2 shrink-0" />
        <span className="flex flex-col">
          <span>{COPY_SUMMARY_LABEL}</span>
          <span className="text-[10px] text-muted-foreground">
            Readable recap to share
          </span>
        </span>
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={async () => done(await copySessionToClipboard(plan))}>
        <FileCode className="h-3.5 w-3.5 mr-2 shrink-0" />
        <span className="flex flex-col">
          <span>{COPY_PLAN_LABEL}</span>
          <span className="text-[10px] text-muted-foreground">
            Loadable in Khronos
          </span>
        </span>
      </DropdownMenuItem>
    </>
  );
};

const CopySessionMenu = ({
  plan,
  // Session-level feedback and the recorded duration for the summary export;
  // absent for a plan that hasn't been run yet.
  notes,
  rating,
  durationMinutes,
  iconOnly = false,
  variant = "outline",
  size = "sm",
  align = "end",
  className = "",
}) => {
  const [copied, setCopied] = useState(false);

  const flash = (ok) => {
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!plan) return null;

  const Icon = copied ? Check : Copy;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`${iconOnly ? "h-8 w-8 p-0" : "gap-1.5"} shrink-0 ${className}`}
          title="Copy this session"
          aria-label="Copy this session"
        >
          <Icon
            className={`h-3.5 w-3.5 ${copied ? "text-green-500" : ""}`}
          />
          {!iconOnly && (copied ? "Copied!" : "Copy")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56">
        <CopySessionItems
          plan={plan}
          notes={notes}
          rating={rating}
          durationMinutes={durationMinutes}
          onCopied={flash}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CopySessionMenu;
